/**
 * Password validation for signup, reset password, and forgot password flows.
 * Requirements: 8–10 chars, upper, lower, number, special; no spaces at ends;
 * only allowed special chars; no 4+ repeats; no 4+ sequential; no common/dictionary;
 * no username/email/phone/name/dob in password.
 */

const ALLOWED_SPECIAL = "!@#$%^&*()_+-=[]{}|;:,.<>?";
const SPECIAL_REGEX = new RegExp(
  `^[A-Za-z0-9${ALLOWED_SPECIAL.replace(/[\]\\^-]/g, "\\$&")}]+$`
);

const COMMON_PASSWORDS = new Set(
  [
    "Password@123",
    "Admin@123",
    "12345678",
    "Password1",
    "Admin123",
    "Welcome1",
    "Welcome123",
    "Qwerty123",
    "Abcd1234",
    "Pass@123",
    "P@ssw0rd",
    "Admin@1",
  ].map(s => s.toLowerCase())
);

const SEQUENCES =
  "abcdefghijklmnopqrstuvwxyz0123456789qwertyuiopasdfghjklzxcvbnm";

const DICTIONARY_WORDS = new Set(
  [
    "password", "admin", "welcome", "qwerty", "letmein", "monkey", "dragon",
    "master", "login", "abc123", "password1", "password123", "admin123",
    "happens", "good", "like", "hello", "sunshine", "princess", "football",
    "iloveyou", "trustno1", "welcome1", "shadow", "ashley", "bailey",
    "passw0rd", "password2", "access", "superman", "qazwsx", "michael",
  ].map(s => s.toLowerCase())
);

export interface PasswordValidationContext {
  email?: string;
  fullName?: string;
  phone?: string;
  username?: string;
  dateOfBirth?: string;
  currentPassword?: string;
  lastPasswords?: string[];
}

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
  hints: { rule: string; met: boolean }[];
}

function hasMoreThanThreeRepeating(password: string): boolean {
  for (let i = 0; i <= password.length - 4; i++) {
    const c = password[i];
    if (password[i + 1] === c && password[i + 2] === c && password[i + 3] === c) {
      return true;
    }
  }
  return false;
}

function hasSequentialPattern(password: string, len: number): boolean {
  const lower = password.toLowerCase();
  for (let i = 0; i <= lower.length - len; i++) {
    const slice = lower.slice(i, i + len);
    const idx = SEQUENCES.indexOf(slice);
    if (idx !== -1) {return true;}
    const rev = [...slice].reverse().join("");
    if (SEQUENCES.includes(rev)) {return true;}
  }
  return false;
}

function extractNameParts(fullName: string): string[] {
  const parts = fullName
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(p => p.length >= 2);
  return [...parts, fullName.trim().toLowerCase()];
}

function containsPartOf(password: string, value: string): boolean {
  if (!value || value.length < 2) {return false;}
  const v = value.toLowerCase().replace(/\s/g, "");
  const p = password.toLowerCase();
  if (p.includes(v)) {return true;}
  if (v.includes(p)) {return true;}
  return false;
}

function looksLikeDob(password: string): boolean {
  const digitsOnly = password.replace(/\D/g, "");
  if (digitsOnly.length < 4) {return false;}
  const ddmmyyyy = /\d{8}/.test(digitsOnly);
  const ddmmyy = /\d{6}/.test(digitsOnly);
  const mmddyy = /\d{6}/.test(digitsOnly);
  if (digitsOnly.length >= 6 && (ddmmyy || mmddyy)) {return true;}
  if (digitsOnly.length >= 8 && ddmmyyyy) {return true;}
  return false;
}

export function validatePassword(
  password: string,
  context: PasswordValidationContext = {}
): PasswordValidationResult {
  const errors: string[] = [];
  const hints: { rule: string; met: boolean }[] = [];

  const p = password;
  const trimmed = p.trim();

  if (trimmed !== p) {
    errors.push("No spaces at the beginning or end");
  }
  hints.push({
    rule: "No spaces at start or end",
    met: trimmed === p,
  });

  if (p.length < 8) {
    errors.push("Password must be at least 8 characters");
  } else if (p.length > 10) {
    errors.push("Password must be at most 10 characters");
  }
  hints.push({
    rule: "8 to 10 characters",
    met: p.length >= 8 && p.length <= 10,
  });

  const hasUpper = /[A-Z]/.test(p);
  const hasLower = /[a-z]/.test(p);
  const hasNumber = /[0-9]/.test(p);
  const hasSpecial = [...ALLOWED_SPECIAL].some(c => p.includes(c));

  if (!hasUpper) {errors.push("At least one UPPER CASE letter (A-Z) required");}
  if (!hasLower) {errors.push("At least one lower case letter (a-z) required");}
  if (!hasNumber) {errors.push("At least one number (0-9) required");}
  if (!hasSpecial) {
    errors.push(
      `At least one special character required: ${ALLOWED_SPECIAL}`
    );
  }
  hints.push({ rule: "One UPPERCASE (A-Z)", met: hasUpper });
  hints.push({ rule: "One lowercase (a-z)", met: hasLower });
  hints.push({ rule: "One number (0-9)", met: hasNumber });
  hints.push({ rule: "One special (!@#$%^&*()_+-=[]{}|;:,.<>?)", met: hasSpecial });

  if (p.length > 0 && !SPECIAL_REGEX.test(p)) {
    errors.push(
      "Only letters, numbers, and these special characters are allowed: !@#$%^&*()_+-=[]{}|;:,.<>?"
    );
  }
  hints.push({
    rule: "Only allowed letters, numbers and specials",
    met: p.length === 0 || SPECIAL_REGEX.test(p),
  });

  if (hasMoreThanThreeRepeating(p)) {
    errors.push("Cannot contain more than 3 repeating characters in a row (e.g. aaaa, 1111)");
  }
  hints.push({
    rule: "No more than 3 repeating chars (e.g. aaaa)",
    met: !hasMoreThanThreeRepeating(p),
  });

  if (p.length >= 4 && hasSequentialPattern(p, 4)) {
    errors.push(
      "Cannot contain sequential patterns of 4+ characters (e.g. abcd, 1234, qwerty)"
    );
  }
  hints.push({
    rule: "No 4+ character sequences (abcd, 1234)",
    met: p.length < 4 || !hasSequentialPattern(p, 4),
  });

  if (COMMON_PASSWORDS.has(p.toLowerCase())) {
    errors.push("Cannot use a common password (e.g. Password@123, Admin@123)");
  }
  hints.push({
    rule: "Not a common password",
    met: !COMMON_PASSWORDS.has(p.toLowerCase()),
  });

  if (DICTIONARY_WORDS.has(p.toLowerCase())) {
    errors.push("Cannot be a dictionary word");
  }
  hints.push({
    rule: "Not a dictionary word",
    met: !DICTIONARY_WORDS.has(p.toLowerCase()),
  });

  if (context.username && containsPartOf(p, context.username)) {
    errors.push("Cannot contain your username");
  }
  if (context.email) {
    const local = context.email.split("@")[0];
    if (local && containsPartOf(p, local)) {
      errors.push("Cannot contain your email address");
    }
  }
  if (context.phone && containsPartOf(p, context.phone.replace(/\D/g, ""))) {
    errors.push("Cannot contain your phone number");
  }
  if (context.fullName) {
    const nameParts = extractNameParts(context.fullName);
    if (nameParts.some(part => part.length >= 2 && p.toLowerCase().includes(part))) {
      errors.push("Cannot contain your first name, last name, or middle name");
    }
  }
  if (looksLikeDob(p)) {
    errors.push("Cannot contain your date of birth (e.g. DDMMYYYY, MMDDYY)");
  }
  hints.push({
    rule: "Does not contain email/name/phone/DOB",
    met:
      !(context.email?.split("@")[0] && containsPartOf(p, context.email.split("@")[0]!)) &&
      !(context.fullName &&
        extractNameParts(context.fullName).some(
          part => part.length >= 2 && p.toLowerCase().includes(part)
        )) &&
      !(context.phone && containsPartOf(p, context.phone.replace(/\D/g, ""))) &&
      !looksLikeDob(p),
  });

  if (context.currentPassword && p === context.currentPassword) {
    errors.push("Cannot be the same as your current password");
  }
  if (context.lastPasswords?.length && context.lastPasswords.some(prev => prev === p)) {
    errors.push("Cannot be the same as any of your last 10 passwords");
  }

  return {
    valid: errors.length === 0,
    errors,
    hints,
  };
}

export function getPasswordRequirementHints(): string[] {
  return [
    "8 to 10 characters",
    "At least one UPPER CASE (A-Z)",
    "At least one lower case (a-z)",
    "At least one number (0-9)",
    "At least one special: !@#$%^&*()_+-=[]{}|;:,.<>?",
    "No spaces at start or end",
    "No more than 3 repeating characters in a row",
    "No sequential patterns (e.g. abcd, 1234, qwerty)",
    "Not a common or dictionary word",
    "Cannot contain email, name, phone, or date of birth",
  ];
}
